/**
 * Weather, heard (the sky shows it since rendering/weatherView.ts; this is the ambient bus's side).
 * Three synthesized layers, always running, mixed by the weather kind — no assets:
 *
 *   - **rain** (67.22; owner, Oct 10: "rain doesn't sound like rain"): a soft broadband wash, a dense bed of
 *     small soft drops (the rain further off), and sparse bright near drops, a few ringing like water — the drops
 *     rendered once into stereo loops (`renderDrops`, pure), so nothing runs per frame; a storm adds a heavier
 *     near layer and a low rumble beneath it. The first cut's patter (bright band noise with a fast flutter) stays
 *     as `rainStyle: 'patter'` for the sound sheet's comparison;
 *   - **wind**: bandpass noise whose centre and volume wander slowly; the kind sets where it sits
 *     (a storm howls high, ash sighs low, fog barely moves);
 *   - **grit**: dull lowpassed noise with an irregular swell — dust on the hood, ash settling.
 *
 * `weatherMix` is the pure table (tests/weatherSound.test.ts); `WeatherSound` is the graph.
 * Levels ramp over `WEATHER_FADE_S` seconds, so a new segment is weather turning, not a cut.
 * Outside the atmosphere the game sets `clear` (the orbit is silent), as it does for the fall.
 */
import type { WeatherKind } from '../core/weather';
import { distantThunder, type WeatherCues } from './weatherAhead';
import { getAudioContext, getAmbientBus } from './sfx';

export interface WeatherMix {
  /** 0–1: the patter's level, and the storm rumble under it (rumble is 0 below a storm) */
  rain: number;
  rumble: number;
  /** 0–1: the wind's level, and the centre of its band (Hz) */
  wind: number;
  windHz: number;
  /** 0–1: the grit's level */
  grit: number;
}

const MIX: Record<WeatherKind, WeatherMix> = {
  clear: { rain: 0, rumble: 0, wind: 0, windHz: 500, grit: 0 },
  haze: { rain: 0, rumble: 0, wind: 0.08, windHz: 500, grit: 0 },
  rain: { rain: 0.7, rumble: 0, wind: 0.15, windHz: 600, grit: 0 },
  snow: { rain: 0, rumble: 0, wind: 0.1, windHz: 350, grit: 0 },   // 74.11 (a): a hush with a little wind
  fog: { rain: 0, rumble: 0, wind: 0.05, windHz: 300, grit: 0 },
  storm: { rain: 1, rumble: 0.6, wind: 0.6, windHz: 900, grit: 0 },
  dust: { rain: 0, rumble: 0, wind: 0.5, windHz: 1400, grit: 0.5 },
  ash: { rain: 0, rumble: 0, wind: 0.2, windHz: 250, grit: 0.25 },
};

/** The layers' levels for a weather kind. */
export function weatherMix(kind: WeatherKind): WeatherMix { return MIX[kind]; }

/**
 * 67.33: the rain's shape — `amount` 0–1 (how much rain: light → a downpour; the kind's own is its `rain` level, 0.7 for
 * rain, 1 for a storm) and `size` 0–1 (small fine drops → big full ones; 0.5 is the drops as rendered). The layers'
 * gains, the drop loops' playback rate (big drops lower and slower, small ones finer and quicker) and two shelves on the
 * rain (big: more low body, less sparkle). At a kind's own amount and size 0.5 it is the 67.22 mix exactly.
 */
export interface RainShape { bus: number; far: number; near: number; heavy: number; wash: number; rate: number; lowDb: number; highDb: number }
export const RAIN_SIZE_DEFAULT = 0.5;
export function rainShape(amount: number, size: number = RAIN_SIZE_DEFAULT): RainShape {
  const a = Math.max(0, Math.min(1, amount)), s = Math.max(0, Math.min(1, size)), r = a / 0.7;
  return {
    bus: a * LAYER_GAIN.rain,
    far: 2 * Math.pow(Math.min(1, r), 1.5),               // the bed of distant drops thickens fastest, full at the rain's own amount
    near: 3 * (0.35 + 0.65 * Math.min(1, r)),             // a light shower is mostly the odd near drop
    heavy: 2 * Math.max(0, (a - 0.7) / 0.3),              // past the rain's own amount, the heavy near drops come in
    wash: 0.2 * Math.min(1, r),                          // (past the rain's own amount: louder, and the heavy drops)
    rate: 1.25 - 0.5 * s,
    lowDb: (s - 0.5) * 10, highDb: (0.5 - s) * 12,
  };
}

/**
 * 67.33: the wind's shape — `strength` 0–1 (its level; the kind's own is its `wind`) and `gust` 0–1 (steady → gusty: the
 * swells' depth and rate, and how far its pitch wanders). The band's centre is the kind's own when given (a storm high,
 * ash low), else it rises with the strength. At a kind's own strength and WIND_GUST_DEFAULT it is the old wind exactly.
 */
export interface WindShape { level: number; hz: number; gustDepth: number; gustHz: number; wander: number }
export const WIND_GUST_DEFAULT = 4 / 9;
export function windShape(strength: number, gust: number = WIND_GUST_DEFAULT, kindHz?: number): WindShape {
  const st = Math.max(0, Math.min(1, strength)), g = Math.max(0, Math.min(1, gust));
  return { level: st * LAYER_GAIN.wind, hz: kindHz ?? 250 + 900 * st, gustDepth: 0.9 * g, gustHz: 0.03 + 0.135 * g, wander: 405 * g };
}

/**
 * 67.26: the wind's howl — a narrow whistling band that comes in only when the wind is strong where you stand (the
 * weather's wind × the gust and the shelter, 62.47): silent under `HOWL_FROM`, then rising; its pitch lifts with the
 * wind. `effective` 0–1 against a storm's wind in the open (can exceed 1 on an exposed crest in a gust).
 */
export const HOWL_FROM = 0.3;
export function howlShape(effective: number): { level: number; hz: number } {
  const e = Math.max(0, effective);
  const over = Math.max(0, (e - HOWL_FROM) / (1 - HOWL_FROM));
  return { level: Math.min(1.2, Math.pow(over, 1.5)), hz: 620 + 520 * Math.min(1.5, e) };
}
/** 67.26: a storm's thunder — the seconds to the next roll (a random draw 0–1 → 9–28 s; presentation, not the sim's streams). */
export function thunderGap(u: number): number { return 9 + 19 * Math.max(0, Math.min(1, u)); }
/** 67.26: snow's hush — the weather's top end closes over snow and ice underfoot (amount 0–1 → 20 kHz … 3.5 kHz, a little quieter). */
export function hushShape(amount: number): { cutoff: number; gain: number } {
  const a = Math.max(0, Math.min(1, amount));
  return { cutoff: 20000 * Math.pow(3500 / 20000, a), gain: 1 - 0.25 * a };
}

/** 67.37: how much the weather drops (a share of its level) in the commander's view: the fight leads, the air sits under it. */
export const FAR_LOWER = 0.55;

/** Seconds a weather change takes to fade between mixes. */
export const WEATHER_FADE_S = 4;
/** The layers' full-level gains on the ambient bus (the mix scales them). */
export const LAYER_GAIN = { rain: 0.11, rumble: 0.08, wind: 0.09, grit: 0.07, howl: 0.05, thunder: 0.32 };

/** A small seeded generator (the drops are the same every run). */
function seeded(seed: number): () => number {
  let a = seed >>> 0;
  return () => { a = (a + 0x6d2b79f5) >>> 0; let t = a; t = Math.imul(t ^ (t >>> 15), t | 1); t ^= t + Math.imul(t ^ (t >>> 7), t | 61); return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
}

/**
 * 67.22: a loop of rain drops, `perSecond` of them over `seconds`, each a tiny decaying tick (1–4 ms) at a random
 * level — the loud ones rarer — and, the brighter the layer, more of them ringing for a moment like water
 * (a damped tone of 1.8–4.6 kHz). `bright` 0 (soft, distant) … 1 (bright, near): the soft ones are low-passed.
 * Wraps at its end, so it loops without a seam; normalised to an RMS of 0.2, or as near as it gets with its peak under
 * 0.95 (never clipped). Pure.
 */
export function renderDrops(sampleRate: number, seconds: number, perSecond: number, bright: number, seed: number): Float32Array {
  const n = Math.max(1, Math.floor(sampleRate * seconds)), out = new Float32Array(n), rnd = seeded(seed);
  const count = Math.round(perSecond * seconds);
  for (let i = 0; i < count; i++) {
    const t0 = Math.floor(rnd() * n), a = 0.15 + 0.85 * rnd() * rnd();
    const len = Math.max(2, Math.floor(sampleRate * (0.001 + 0.003 * rnd()))), tau = len / 3;
    for (let k = 0; k < len; k++) out[(t0 + k) % n] += a * (rnd() * 2 - 1) * Math.exp(-k / tau);
    if (rnd() < 0.3 * bright) {
      const f = 1800 + 2800 * rnd(), L = Math.floor(sampleRate * (0.008 + 0.012 * rnd()));
      for (let k = 0; k < L; k++) out[(t0 + k) % n] += 0.35 * a * Math.sin((2 * Math.PI * f * k) / sampleRate) * Math.exp(-k / (L / 4));
    }
  }
  if (bright < 1) {
    // a one-pole low-pass, run round the loop twice so its state at the wrap is settled (no click at the seam)
    const alpha = 0.12 + 0.85 * Math.max(0, bright);
    let y = 0;
    for (let pass = 0; pass < 2; pass++) for (let i = 0; i < n; i++) { y += alpha * (out[i] - y); if (pass === 1) out[i] = y; }
  }
  let sq = 0, peak = 0;
  for (let i = 0; i < n; i++) { sq += out[i] * out[i]; peak = Math.max(peak, Math.abs(out[i])); }
  const rms = Math.sqrt(sq / n);
  // to an RMS of 0.2 — or as near as it gets without clipping (sparse bright drops are all peak)
  const k = rms > 0 ? Math.min(0.2 / rms, 0.95 / peak) : 0;
  for (let i = 0; i < n; i++) out[i] *= k;
  return out;
}

/** A looping two-channel drop layer (each channel its own drops: the rain all round). */
function dropLoop(ctx: AudioContext, seconds: number, perSecond: number, bright: number, seed: number): AudioBufferSourceNode {
  const len = Math.max(1, Math.floor(ctx.sampleRate * seconds));
  const buf = ctx.createBuffer(2, len, ctx.sampleRate);
  buf.getChannelData(0).set(renderDrops(ctx.sampleRate, seconds, perSecond, bright, seed));
  buf.getChannelData(1).set(renderDrops(ctx.sampleRate, seconds, perSecond, bright, seed + 7919));
  const src = ctx.createBufferSource();
  src.buffer = buf;
  src.loop = true;
  return src;
}

/** 67.22: how the rain is made — the drops (the game's) or the first cut's patter (kept for the sound sheet). */
export type RainStyle = 'drops' | 'patter';

function loopingNoise(ctx: AudioContext, seconds: number): AudioBufferSourceNode {
  const len = Math.floor(ctx.sampleRate * seconds);
  const buf = ctx.createBuffer(1, len, ctx.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
  const src = ctx.createBufferSource();
  src.buffer = buf;
  src.loop = true;
  return src;
}

export class WeatherSound {
  private readonly ctx: AudioContext;
  private readonly nodes: AudioNode[] = [];
  private readonly sources: Array<AudioScheduledSourceNode> = [];
  private readonly rain: GainNode;
  private readonly rumble: GainNode;
  private readonly wind: GainNode;
  private readonly windBand: BiquadFilterNode;
  private readonly grit: GainNode;
  /** 67.22: the storm's heavier near drops (0 below a storm) */
  private readonly heavy: GainNode | null = null;
  /** 67.33: the drop layers, their loops and the rain's two shelves (the drops style; null for the patter) */
  private readonly drops: { far: GainNode; near: GainNode; wash: GainNode; loops: AudioBufferSourceNode[]; low: BiquadFilterNode; high: BiquadFilterNode } | null = null;
  /** 67.33: the wind's gust swell (its rate and depth) and its pitch wander (depth) */
  private readonly gustLfo: { osc: OscillatorNode; depth: GainNode };
  private readonly wanderDepth: GainNode;
  /** 67.26: the howl (two narrow bands, the second an octave and a fifth up), the snow's hush on the way out, the thunder's timer */
  private readonly howl: GainNode;
  private readonly howlBands: BiquadFilterNode[];
  private readonly hushFilter: BiquadFilterNode;
  private readonly hushGain: GainNode;
  private readonly dest: AudioNode;
  private thunderTimer: ReturnType<typeof setTimeout> | null = null;
  /** 67.38: weather coming — a far rain's hiss and a dust front's grit (each panned to its side), the storm outside */
  private readonly aheadRain: { gain: GainNode; side: StereoPannerNode };
  private readonly aheadGrit: { gain: GainNode; side: StereoPannerNode };
  private aheadStorm: { rad: number; pan: number; radiusMetres: number } | null = null;
  /** 67.33: the sound board's hand on the rain and the wind (null: the weather kind's own) */
  private rainHand: { amount: number | null; size: number | null } = { amount: null, size: null };
  private windHand: { strength: number | null; gust: number | null } = { strength: null, gust: null };
  private kind: WeatherKind = 'clear';

  /** Builds the three layers silent on the ambient bus; `ctx`/`bus` default to the game's (throws when audio is not initialised). */
  constructor(ctx: AudioContext | null = getAudioContext(), bus: AudioNode | null = getAmbientBus(), rainStyle: RainStyle = 'drops') {
    if (!ctx || !bus) throw new Error('Audio context not initialized — call initAudio() first');
    this.ctx = ctx;
    const gain = (v: number) => { const g = ctx.createGain(); g.gain.value = v; this.nodes.push(g); return g; };
    const filter = (type: BiquadFilterType, hz: number, q: number) => { const f = ctx.createBiquadFilter(); f.type = type; f.frequency.value = hz; f.Q.value = q; this.nodes.push(f); return f; };
    const lfo = (hz: number, depth: number, target: AudioParam) => { const o = ctx.createOscillator(); o.type = 'sine'; o.frequency.value = hz; const g = gain(depth); o.connect(g); g.connect(target); this.sources.push(o); this.nodes.push(o); return { osc: o, depth: g }; };

    // 67.26: every layer goes out through the snow's hush (a low-pass, open by default) to the ambient bus
    this.hushFilter = filter('lowpass', 20000, 0.5); this.hushGain = gain(1);
    this.hushFilter.connect(this.hushGain); this.hushGain.connect(bus);
    const dest = this.dest = this.hushFilter;
    this.rain = gain(0); this.rain.connect(dest);
    if (rainStyle === 'patter') {
      // the first cut: bright band, fluttering fast
      const rainSrc = loopingNoise(ctx, 7), rainBand = filter('bandpass', 2600, 0.6);
      const flutter = gain(1);
      rainSrc.connect(rainBand); rainBand.connect(flutter); flutter.connect(this.rain);
      lfo(7.3, 0.35, flutter.gain);
      this.sources.push(rainSrc); this.nodes.push(rainSrc);
    } else {
      // 67.33: the drops' size as tone — two shelves on the rain (0 dB at the default size: transparent)
      const low = ctx.createBiquadFilter(); low.type = 'lowshelf'; low.frequency.value = 400; low.gain.value = 0; this.nodes.push(low);
      const high = ctx.createBiquadFilter(); high.type = 'highshelf'; high.frequency.value = 3000; high.gain.value = 0; this.nodes.push(high);
      const tone = gain(1);
      tone.connect(low); low.connect(high); high.connect(this.rain);
      // 67.22: a soft broadband wash under it all
      const washSrc = loopingNoise(ctx, 7), washHi = filter('highpass', 300, 0.5), washLo = filter('lowpass', 5500, 0.5), wash = gain(0.2);
      washSrc.connect(washHi); washHi.connect(washLo); washLo.connect(wash); wash.connect(tone);
      // the rain further off: a dense bed of small soft drops
      const farSrc = dropLoop(ctx, 4, 500, 0.25, 11), farLo = filter('lowpass', 4000, 0.5), far = gain(2);
      farSrc.connect(farLo); farLo.connect(far); far.connect(tone);
      // near drops: sparse and bright, some ringing
      // (the drop loops come out quiet — never clipped, they are mostly peak — so their gains lift them: the drops lead, the wash sits under)
      const nearSrc = dropLoop(ctx, 6, 18, 1, 23), near = gain(3);
      nearSrc.connect(near); near.connect(tone);
      // a storm's heavier near layer
      const heavySrc = dropLoop(ctx, 5, 60, 0.8, 37); this.heavy = gain(0);
      heavySrc.connect(this.heavy); this.heavy.connect(tone);
      this.drops = { far, near, wash, loops: [farSrc, nearSrc, heavySrc], low, high };
      for (const s of [washSrc, farSrc, nearSrc, heavySrc]) { this.sources.push(s); this.nodes.push(s); }
    }
    // rumble: a low roll under a storm
    const rumbleSrc = loopingNoise(ctx, 9), rumbleLow = filter('lowpass', 120, 1.2);
    this.rumble = gain(0);
    rumbleSrc.connect(rumbleLow); rumbleLow.connect(this.rumble); this.rumble.connect(dest);
    lfo(0.17, 0.5, this.rumble.gain);
    this.sources.push(rumbleSrc); this.nodes.push(rumbleSrc);
    // wind: a wandering band
    const windSrc = loopingNoise(ctx, 11); this.windBand = filter('bandpass', 500, 1.6);
    const gust = gain(1); this.wind = gain(0);
    windSrc.connect(this.windBand); this.windBand.connect(gust); gust.connect(this.wind); this.wind.connect(dest);
    this.wanderDepth = lfo(0.05, 180, this.windBand.frequency).depth;
    this.gustLfo = lfo(0.09, 0.4, gust.gain);
    this.sources.push(windSrc); this.nodes.push(windSrc);
    // grit: dull, swelling irregularly
    const gritSrc = loopingNoise(ctx, 13), gritLow = filter('lowpass', 800, 0.8);
    const swell = gain(1); this.grit = gain(0);
    gritSrc.connect(gritLow); gritLow.connect(swell); swell.connect(this.grit); this.grit.connect(dest);
    // 67.26: the howl — the wind through gaps and over edges, only when it is strong where you stand
    const howlSrc = loopingNoise(ctx, 12); this.howl = gain(0);
    this.howlBands = [filter('bandpass', 900, 9), filter('bandpass', 1350, 12)];
    for (const b of this.howlBands) { howlSrc.connect(b); b.connect(this.howl); }
    this.howl.connect(dest);
    // 67.38: weather coming, each layer panned to the cell's side: a far rain (a soft dull hiss) and a dust front's grit
    const aheadLayer = (src: AudioBufferSourceNode, lo: number, hi: number) => {
      const hp = filter('highpass', lo, 0.5), lp = filter('lowpass', hi, 0.5), g = gain(0), side = ctx.createStereoPanner();
      this.nodes.push(side);
      src.connect(hp); hp.connect(lp); lp.connect(g); g.connect(side); side.connect(dest);
      this.sources.push(src); this.nodes.push(src);
      return { gain: g, side };
    };
    this.aheadRain = aheadLayer(loopingNoise(ctx, 8), 250, 1700);
    this.aheadGrit = aheadLayer(loopingNoise(ctx, 9), 120, 650);
    lfo(0.21, 60, this.howlBands[0].frequency); lfo(0.13, 90, this.howlBands[1].frequency);
    this.sources.push(howlSrc); this.nodes.push(howlSrc);
    lfo(0.31, 0.5, swell.gain);
    this.sources.push(gritSrc); this.nodes.push(gritSrc);

    for (const s of this.sources) s.start();
  }

  /** The weather now; idempotent and cheap, so the game calls it every frame. */
  set(kind: WeatherKind): void {
    if (kind === this.kind) return;
    this.kind = kind;
    this.apply(WEATHER_FADE_S);
    // 67.26: thunder rolls under a storm, a while apart
    this.keepThunder();
  }

  /** 67.26: snow's hush (0–1): over snow or ice the weather's top end closes and it sits a little lower. */
  hush(amount: number): void {
    if (Math.abs(amount - this.hushAmount) < 0.01) return;
    this.hushAmount = amount;
    this.applyOut();
  }

  /** 67.37: the weather lowered while the commander's view looks down on the fight (amount 0 the shoulder … 1 the view). */
  far(amount: number): void {
    if (Math.abs(amount - this.farAmount) < 0.01) return;
    this.farAmount = amount;
    this.applyOut();
  }

  private hushAmount = 0;
  private farAmount = 0;
  private applyOut(): void {
    const h = hushShape(this.hushAmount), t = this.ctx.currentTime;
    this.hushFilter.frequency.setTargetAtTime(h.cutoff, t, 0.8);
    this.hushGain.gain.setTargetAtTime(h.gain * (1 - FAR_LOWER * Math.max(0, Math.min(1, this.farAmount))), t, 0.4);
  }

  /**
   * 67.26: one roll of thunder — mostly far (a long low roll, a few swells in it), now and then near (a crack first).
   * Each a few nodes made for it and let go when it has rolled out.
   */
  thunderNow(near = Math.random() < 0.25): void {
    const ctx = this.ctx, t = ctx.currentTime + 0.05, len = near ? 4 : 5.5;
    const src = loopingNoise(ctx, 7), low = ctx.createBiquadFilter(), g = ctx.createGain();
    low.type = 'lowpass'; low.frequency.setValueAtTime(near ? 900 : 320, t); low.frequency.exponentialRampToValueAtTime(110, t + len);
    g.gain.setValueAtTime(0, t);
    const peak = LAYER_GAIN.thunder * (near ? 1 : 0.6);
    g.gain.linearRampToValueAtTime(peak, t + (near ? 0.04 : 0.6));
    for (let i = 1; i <= 3; i++) g.gain.linearRampToValueAtTime(peak * (0.45 + 0.4 * Math.random()) / i, t + i * len / 4);   // the roll's swells
    g.gain.linearRampToValueAtTime(0, t + len);
    src.connect(low); low.connect(g); g.connect(this.dest);
    src.start(t); src.stop(t + len + 0.1);
    if (near) {
      const crack = loopingNoise(ctx, 1), hi = ctx.createBiquadFilter(), cg = ctx.createGain();
      hi.type = 'highpass'; hi.frequency.value = 1800;
      cg.gain.setValueAtTime(peak * 0.7, t); cg.gain.exponentialRampToValueAtTime(0.001, t + 0.35);
      crack.connect(hi); hi.connect(cg); cg.connect(this.dest);
      crack.start(t); crack.stop(t + 0.4);
    }
  }

  private scheduleThunder(): void {
    this.thunderTimer = setTimeout(() => {
      this.thunderTimer = null;
      if (this.kind === 'storm') this.thunderNow();
      else if (this.aheadStorm) this.thunderFrom(this.aheadStorm);   // 67.38: a storm outside, from its side
      else return;
      this.scheduleThunder();
    }, thunderGap(Math.random()) * 1000);
  }

  /** Thunder rolls while a storm is overhead or (67.38) a storm cell's thunder carries; stops when neither. */
  private keepThunder(): void {
    const want = this.kind === 'storm' || this.aheadStorm !== null;
    if (want && this.thunderTimer === null) this.scheduleThunder();
    if (!want && this.thunderTimer !== null) { clearTimeout(this.thunderTimer); this.thunderTimer = null; }
  }

  /**
   * 67.38: the weather coming (weatherAhead.ts `weatherCues` from the planet's cells; null = none, the weather cells off):
   * the far rain and the dust front's grit swell from their sides as the cells near; a storm outside rolls its thunder.
   * `radiusMetres`: the planet's radius in metres (the thunder's delay). Called a few times a second.
   */
  ahead(cues: WeatherCues | null, radiusMetres = 0): void {
    const t = this.ctx.currentTime;
    const set = (l: { gain: GainNode; side: StereoPannerNode }, level: number, pan: number, full: number) => {
      l.gain.gain.setTargetAtTime(level * full, t, 1.2);
      l.side.pan.setTargetAtTime(pan, t, 0.5);
    };
    set(this.aheadRain, cues?.rain.level ?? 0, cues?.rain.pan ?? 0, LAYER_GAIN.rain * 0.7);
    set(this.aheadGrit, cues?.grit.level ?? 0, cues?.grit.pan ?? 0, LAYER_GAIN.grit * 0.9);
    this.aheadStorm = cues?.storm ? { ...cues.storm, radiusMetres } : null;
    this.keepThunder();
  }

  /** 67.38: a roll from a storm cell outside: late by its distance, duller and softer far off, from its side. */
  thunderFrom(s: { rad: number; pan: number; radiusMetres: number }): void {
    const d = distantThunder(s.rad, s.radiusMetres);
    const ctx = this.ctx, t = ctx.currentTime + 0.05 + d.delay, len = 5.5;
    const src = loopingNoise(ctx, 7), low = ctx.createBiquadFilter(), g = ctx.createGain(), side = ctx.createStereoPanner();
    low.type = 'lowpass'; low.frequency.setValueAtTime(d.cutoff, t); low.frequency.exponentialRampToValueAtTime(80, t + len);
    side.pan.value = s.pan;
    const peak = LAYER_GAIN.thunder * d.level;
    g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(peak, t + 0.9);
    for (let i = 1; i <= 3; i++) g.gain.linearRampToValueAtTime(peak * (0.45 + 0.4 * Math.random()) / i, t + i * len / 4);
    g.gain.linearRampToValueAtTime(0, t + len);
    src.connect(low); low.connect(g); g.connect(side); side.connect(this.dest);
    src.start(t); src.stop(t + len + 0.1);
  }

  /**
   * 67.33: the sound board's hand on the rain — how much (0–1) and how big the drops (0–1); null for either follows the
   * weather kind (its own amount; the default size). The game never calls it.
   */
  setRain(amount: number | null, size: number | null): void { this.rainHand = { amount, size }; this.apply(0.3); }
  /** 67.33: the board's hand on the wind — strength and gustiness (0–1 each); null follows the weather kind. */
  setWind(strength: number | null, gust: number | null): void { this.windHand = { strength, gust }; this.apply(0.3); }

  /** The kind's mix with the board's hand laid over, ramped over `fade` seconds. */
  private apply(fade: number): void {
    const m = weatherMix(this.kind), t = this.ctx.currentTime;
    const ramp = (p: AudioParam, v: number) => { p.cancelScheduledValues(t); p.setValueAtTime(p.value, t); p.linearRampToValueAtTime(v, t + fade); };
    const rain = rainShape(this.rainHand.amount ?? m.rain, this.rainHand.size ?? RAIN_SIZE_DEFAULT);
    ramp(this.rain.gain, rain.bus);
    if (this.heavy) ramp(this.heavy.gain, rain.heavy);   // 67.22: only a storm (rain 1) brings the heavy drops
    if (this.drops) {
      ramp(this.drops.far.gain, rain.far); ramp(this.drops.near.gain, rain.near); ramp(this.drops.wash.gain, rain.wash);
      ramp(this.drops.low.gain, rain.lowDb); ramp(this.drops.high.gain, rain.highDb);
      for (const l of this.drops.loops) ramp(l.playbackRate, rain.rate);
    }
    ramp(this.rumble.gain, m.rumble * LAYER_GAIN.rumble);
    const wind = windShape(this.windHand.strength ?? m.wind, this.windHand.gust ?? WIND_GUST_DEFAULT, this.windHand.strength === null ? m.windHz : undefined);
    ramp(this.wind.gain, wind.level * this.gustFactor);   // 62.47: × the gust and the shelter where the listener is (`gust`)
    ramp(this.windBand.frequency, wind.hz);
    ramp(this.gustLfo.depth.gain, wind.gustDepth); ramp(this.gustLfo.osc.frequency, wind.gustHz); ramp(this.wanderDepth.gain, wind.wander);
    ramp(this.grit.gain, m.grit * LAYER_GAIN.grit * (0.5 + 0.5 * Math.min(1.5, this.gustFactor)));
    this.applyHowl(fade);
  }

  /** 67.26: the howl and the grit follow the wind where you stand: its level × the gust and the shelter. */
  private applyHowl(fade: number): void {
    const m = weatherMix(this.kind), t = this.ctx.currentTime;
    const strength = this.windHand.strength ?? m.wind;
    const h = howlShape((strength / (weatherMix('storm').wind || 1)) * this.gustFactor);
    const ramp = (p: AudioParam, v: number) => { p.cancelScheduledValues(t); p.setValueAtTime(p.value, t); p.linearRampToValueAtTime(v, t + fade); };
    ramp(this.howl.gain, h.level * LAYER_GAIN.howl);
    ramp(this.howlBands[0].frequency, h.hz); ramp(this.howlBands[1].frequency, h.hz * 1.5);
  }

  /**
   * 62.47: the gust and the shelter where the listener is (`factor` from core/wind.ts windHere: 1 the weather's usual
   * wind, less in a hollow or a stand, more on a crest). The wind layer chases its level (the kind's, or the sound board's hand) × it, eased so a gust
   * swells rather than steps; called each drawn frame, it only moves the target when the factor has moved.
   */
  private gustFactor = 1;
  gust(factor: number): void {
    const f = Math.max(0, Math.min(2, factor));
    if (Math.abs(f - this.gustFactor) < 0.02) return;
    this.gustFactor = f;
    const p = this.wind.gain, t = this.ctx.currentTime;
    const m = weatherMix(this.kind);
    const level = windShape(this.windHand.strength ?? m.wind, this.windHand.gust ?? WIND_GUST_DEFAULT, this.windHand.strength === null ? m.windHz : undefined).level;
    p.cancelScheduledValues(t); p.setValueAtTime(p.value, t); p.linearRampToValueAtTime(level * f, t + 1.5);
    // 67.26: the grit swells with the gusts (sand and ash on the hood), the howl comes in when it is strong
    const gg = this.grit.gain; gg.cancelScheduledValues(t); gg.setValueAtTime(gg.value, t); gg.linearRampToValueAtTime(m.grit * LAYER_GAIN.grit * (0.5 + 0.5 * Math.min(1.5, f)), t + 1.5);
    this.applyHowl(1.5);
  }

  get current(): WeatherKind { return this.kind; }

  cleanup(): void {
    if (this.thunderTimer !== null) { clearTimeout(this.thunderTimer); this.thunderTimer = null; }
    for (const s of this.sources) { try { s.stop(); } catch { /* already stopped */ } }
    for (const n of this.nodes) n.disconnect();
    this.sources.length = 0; this.nodes.length = 0;
  }
}
